import { FormProvider, H7, Input, useForm } from '@qred/components-library';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ValidationContext } from '~/components/hoc/withValidation';
import { phoneNumberCountryCodes } from '~/constants/markets';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { ICompanySearchResult } from '~/interfaces/shared';
import { RootState } from '~/store';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';
import {
  updateApplicantData,
  updateForm,
} from '~/store/slices/cardApplication.slice';
import { CompanySearch } from './components/CompanySearch';
import {
  StartApplicationForm,
  createStartApplicationSchema,
} from './StartApplication.schema';

export const StartApplication = () => {
  const {
    organizationNumber,
    companyName,
    companyTypeCategory,
    companyType,
    creditReportProvider,
    companyAddress,
  } = useSelector((state: RootState) => state.cardApplication.form);
  const { phone, email } = useSelector(
    (state: RootState) => state.cardApplication.form.applicant
  );
  const validationContext = useContext(ValidationContext);
  const dispatch = useAppDispatch();
  const market = useSelector((state: RootState) => state.intl.market);
  const t = useTranslate();

  const [
    companyDetails,
    setCompanyDetails,
  ] = useState<ICompanySearchResult | null>(
    organizationNumber || creditReportProvider?.creditReportProviderNumber
      ? {
          companyName: companyName || '',
          companyNumber: organizationNumber || '',
          companyType,
          companyTypeCategory,
          creditReportProvider,
          address: companyAddress && {
            street: companyAddress.street ?? '',
            postcode: companyAddress.zipCode ?? '',
            city: companyAddress.city ?? '',
          },
        }
      : null
  );

  const formMethods = useForm<StartApplicationForm>({
    schema: createStartApplicationSchema(market),
    defaultValues: {
      selectedCompany:
        organizationNumber ||
        creditReportProvider?.creditReportProviderNumber ||
        '',
      phone: phone || phoneNumberCountryCodes[market],
      email: email || '',
    },
    mode: 'onTouched',
  });

  const {
    watch,
    register,
    formState: { isValid, errors },
  } = formMethods;

  const phoneValue = watch('phone');
  const emailValue = watch('email');

  useEffect(() => {
    if (!isValid) {
      validationContext.manuallyInvalidateForm(true);
    }

    return () => {
      validationContext.manuallyInvalidateForm(false);
    };
  }, [isValid]);

  useEffect(() => {
    if (isValid && companyDetails) {
      dispatch(
        updateForm({
          organizationNumber: companyDetails.companyNumber,
          companyName: companyDetails.companyName,
          companyTypeCategory: companyDetails.companyTypeCategory,
          companyType: companyDetails.companyType,
          creditReportProvider: companyDetails.creditReportProvider,
          companyAddress: companyDetails.address && {
            street: companyDetails.address.street,
            zipCode: companyDetails.address.postcode,
            city: companyDetails.address.city,
          },
        })
      );

      dispatch(updateApplicantData({ phone: phoneValue, email: emailValue }));
    }
  }, [isValid, companyDetails, phoneValue, emailValue, dispatch]);

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    dispatch(
      trackCardApplicationActions({
        actionName: `${e.target.name}_change`,
      })
    );
  };

  return (
    <FormProvider {...formMethods}>
      <H7>{t('CardApplication.CompanyDetailsTitle')}</H7>
      <CompanySearch
        defaultCompanyDetails={companyDetails}
        setCompanyDetails={setCompanyDetails}
      />
      <H7>{t('CardApplication.ContactInformationTitle')}</H7>
      <Input.Group orientation="vertical">
        <Input
          {...register('email', { onBlur })}
          label={t('Email')}
          error={errors?.email?.message && t(errors.email.message)}
          id="email"
        />
        <Input
          {...register('phone', { onBlur })}
          label={t('MobilePhone')}
          error={errors?.phone?.message && t(errors.phone.message)}
          id="phone"
        />
      </Input.Group>
    </FormProvider>
  );
};
